import { Controller } from "@hotwired/stimulus"
import { savePlan, debounce, isSectionIncluded, matchesServiceLink, registerFlush, flushAll } from "plan_storage"
import { syncWhoLivesIntoCircle, dedupeCircle, normCosName } from "who_lives_link"
import { showBulkModal } from "bulk_modal"
import { renderIcons } from "lucide_icons"
import { saveGoalToLibrary } from "goal_library"

// Canonical section order — mirrors EditorController::SECTION_KEYS (the
// server list that renders the section tree). Fallback for the "12.1"-style
// goal badge when the sidebar DOM can't be read. Goals is position 12.
const DEFAULT_SECTION_ORDER = [
  "client", "about", "circleOfSupport", "communication", "wonderfulThings",
  "notSoFavorites", "dayInLife", "important", "bigDreams", "supportMatch",
  "working", "goals", "barriers", "currentServices", "unmetNeeds",
  "resourceCalendar", "review", "facilitator"
]

// Dynamic-list controller — generic add/edit/delete/reorder backing
// for any section that's a list of `{ id, text }` items keyed at a
// dotted plan path (e.g. "about.whoLivesWithMe", or just "bigDreams"
// when the section value is itself an array).
//
// Mirrors the iframe editor's renderDynamicList (pcp_builder.html#5170)
// for the common case — without yet handling linked-strength groups,
// structured/linked service rows, or starred items. Those advanced
// behaviors arrive when the sections that need them are ported.
//
// Usage on the partial:
//
//   <div data-controller="dynamic-list"
//        data-dynamic-list-path-value="about.whoLivesWithMe"
//        data-dynamic-list-placeholder-value="e.g. Mom, Dad, Sibling">
//     <div data-dynamic-list-target="list"></div>
//     <button data-action="dynamic-list#add">+ Add</button>
//     <template data-dynamic-list-target="rowTemplate"> … </template>
//   </div>

const STORAGE_KEY = "pcp_plan_data"

// Whole-word "TBD" (case-insensitive), matching pcp_builder.html#1593
// hasTBD. Used to flag rows whose fields still need filling in.
const TBD_RX = /\bTBD\b/i

// Circle of Support relationship <select> option values (see
// _circle_of_support.html.erb). Bulk-add snaps a pasted relationship to
// the matching option (case-insensitive) so the dropdown, stored value,
// and category icon stay in sync; unknown values fall back to TBD.
const COS_RELATIONSHIPS = ["Family", "Friend", "Professional", "Community", "Service Dog", "TBD"]

// Mirrors print_preview_controller.js LWM_RELATIONSHIPS so the chip
// shown on whoLivesWithMe rows lines up with the chip the preview
// renders. Keep these two lists in sync.
const DL_LWM_RELATIONSHIPS = [
  { match: /\bmother|\bmom\b|\bmama\b|\bmum\b/i,           label: "Mother",      color: "rose"    },
  { match: /\bfather|\bdad\b|\bpapa\b|\bdaddy\b/i,         label: "Father",      color: "blue"    },
  { match: /\bgrand\s*mother|\bgrandma\b|\bnana\b|\bgran\b/i, label: "Grandmother", color: "rose"  },
  { match: /\bgrand\s*father|\bgrandpa\b/i,                label: "Grandfather", color: "sky"     },
  { match: /\bgrandparent/i,                               label: "Grandparent", color: "amber"   },
  { match: /\bstep\s*mother|\bstepmom\b/i,                 label: "Stepmother",  color: "rose"    },
  { match: /\bstep\s*father|\bstepdad\b/i,                 label: "Stepfather",  color: "blue"    },
  { match: /\bsister\b/i,                                  label: "Sister",      color: "pink"    },
  { match: /\bbrother\b/i,                                 label: "Brother",     color: "indigo"  },
  { match: /\bson\b/i,                                     label: "Son",         color: "blue"    },
  { match: /\bdaughter\b/i,                                label: "Daughter",    color: "pink"    },
  { match: /\b(spouse|partner|husband|wife)\b/i,           label: "Partner",     color: "red"     },
  { match: /\b(aunt|auntie)\b/i,                           label: "Aunt",        color: "teal"    },
  { match: /\buncle\b/i,                                   label: "Uncle",       color: "teal"    },
  { match: /\bcousin\b/i,                                  label: "Cousin",      color: "amber"   },
  { match: /\b(roommate|housemate|flatmate)\b/i,           label: "Roommate",    color: "violet"  },
  { match: /\b(caregiver|attendant|aide|caretaker|nurse|support staff|staff)\b/i, label: "Caregiver", color: "emerald" },
  { match: /\b(friend|pal|buddy)\b/i,                      label: "Friend",      color: "yellow"  },
  { match: /\b(pet|dog|cat|bird)\b/i,                      label: "Pet",         color: "amber"   }
]
const DL_SCHOOL_TYPES = [
  { match: /\b(?:pre[-\s]?school|nursery)\b/i,             label: "Preschool",    color: "rose"    },
  { match: /\b(?:pre[-\s]?k|kindergarten)\b/i,             label: "Kindergarten", color: "amber"   },
  { match: /\b(?:elementary|primary)\s*(?:school)?\b/i,    label: "Elementary",   color: "emerald" },
  { match: /\b(?:middle|junior\s*high)\s*(?:school)?\b/i,  label: "Middle",       color: "blue"    },
  { match: /\b(?:high\s*school|senior\s*high)\b/i,         label: "High",         color: "indigo"  },
  { match: /\b(?:college|university)\b/i,                  label: "College",      color: "violet"  },
  { match: /\b(?:home[\s-]?school|homeschool|charter)\b/i, label: "Charter",      color: "teal"    },
  { match: /\b(?:vocational|trade\s*school|tech\b)\b/i,    label: "Vocational",   color: "amber"   }
]
const DL_GRADE_RX = /\b(?:pre[-\s]?k|kindergarten|grade\s*k\b|grade\s*\d{1,2}|\d{1,2}(?:st|nd|rd|th)(?:\s*grade)?|freshman|sophomore|junior|senior)\b/i

export default class extends Controller {
  static targets = ["list", "rowTemplate", "empty", "clearAll", "count"]
  static values  = {
    path:         String,
    placeholder:  String,
    // Pipe-separated field names for multi-column bulk-add. When set,
    // openBulk() splits each pasted line by "|" and assigns the
    // segments to these fields in order (matches the bulk-add modal
    // in pcp_builder.html#11256 for communication / circle of
    // support). Leave empty to fall back to the single-text mode.
    bulkFields:        String,
    bulkPlaceholder:   String,
    // Singular/plural label used in the "· N items" subtitle next to
    // the section header. Defaults to "items"; sections can override
    // ("members", "styles", "schools") for tighter copy.
    itemsLabel:        { type: String, default: "items" },
    // When set ("lwm" or "school"), each row's text is parsed and
    // small chips appear next to the input — e.g. "Mother" for
    // whoLivesWithMe or "Elementary" + "4th" for schoolDetails.
    // The textarea remains the source of truth; chips are read-only
    // display projections that update as the user types.
    parseMode:         String,
    // Circle-of-Support grouping (see CircleOfSupportGroups): the ONE
    // about.circleOfSupport array is split into per-group lists. Filtering is
    // display-only — the STORED array is always the full array.
    relationshipFilter:  String,   // "Family"|"Friend"|"Professional"|"Community"|"__rest__"; "" = show all
    defaultRelationship: String,   // relationship stamped on rows added in this group
    excludeLinked:       { type: Boolean, default: false }, // hide linkedFromWhoLives rows
    ownsHouseholdSync:   { type: Boolean, default: false }  // only this instance seeds the household mirror
  }

  connect() {
    this.plan = this._load()
    this._ensureArray()
    // Track rows that were just added (Add Row / Bulk Add) so the
    // NEW pill shows until the user edits one of the row's fields,
    // mirroring appState._newIds in pcp_builder.html.
    this._newIds = new Set()
    // Issue #2: Circle of Support auto-links household members from
    // "Who Lives With Me?". Seed before render so the first paint
    // already shows linked rows. Only the sync-owning instance runs it, so the
    // grouped lists don't each re-seed the same mirror rows.
    if (this.pathValue === "about.circleOfSupport" && this.ownsHouseholdSyncValue) this._syncFromHousehold()
    this.render()
    // Preview → editor jump: the goal edit button links to
    // /editor/native/goals#ref-goal-N; scroll the Nth goal card into view.
    if (this.pathValue === "goals") this._scrollToHashGoal()
    // Track when other plan-change events affect this path so the list
    // stays in sync if the underlying data is mutated elsewhere (e.g.
    // an Import .sdp action). Skip self-fired events — re-rendering
    // in response to our own update() would orphan the input the user
    // is currently editing.
    this._onPlanChange = (e) => {
      if (this._suppressNextChange) return
      // Leaf-field updates from a nested controller (e.g. goal-resources
      // typing into Vendor Details) carry nested:true. Re-rendering the
      // outer list on every keystroke yanks the focused input — bail.
      if (e?.detail?.nested) return
      const p = e.detail && e.detail.path
      if (!p) return
      // Wildcard path means an undo/redo restored an entire plan
      // snapshot — every list controller needs to re-render to reflect
      // the restored state.
      if (p === "*") {
        this.plan = e.detail.plan || this._load()
        this._ensureArray()
        this.render()
        return
      }
      // Redact toggle or participant-name change: re-resolve the DISPLAY of
      // any [FirstName]-bearing content fields (goal descriptions) in place.
      // DATA-LOSS GUARD: do NOT adopt the event's whole plan here. An emitter
      // that snapshots at connect (plan_modes) can broadcast a STALE plan, and
      // replacing this.plan would let our next save clobber our own freshly
      // edited goals/barriers with the stale array. Refresh ONLY the
      // display-relevant client/meta subtrees from fresh storage; never touch
      // this.plan's owned array.
      if (p === "meta.redacted" || p === "client.firstName" || p === "client.name") {
        const fresh = this._load()
        if (fresh && fresh.client) this.plan.client = fresh.client
        if (fresh && fresh.meta)   this.plan.meta   = fresh.meta
        this._reresolveNameTokenFields()
        return
      }
      const ownPath = (p === this.pathValue || p.startsWith(`${this.pathValue}.`))
      // currentServices keeps linked rows whose RC / Generic tag is
      // owned by goals or barriers — re-render when those branches
      // change too so the locked tag select picks up source flips.
      const linkedSrcChanged = this.pathValue === "currentServices" &&
        (p === "goals" || p.startsWith("goals.") || p === "barriers" || p.startsWith("barriers."))
      // Issue #2: Circle of Support follows About.whoLivesWithMe.
      // When the household list changes, re-sync the linked rows so
      // adding / removing / renaming a household member flows through.
      const householdChanged = this.pathValue === "about.circleOfSupport" &&
        (p === "about.whoLivesWithMe" || p.startsWith("about.whoLivesWithMe."))
      // Toggling "Behavioral Communication" flips the Behavior Communication
      // Chart's default inclusion, which shifts the Goals section number — so
      // the goals list must repaint its "13.1" badges when it changes.
      const commStyleChanged = this.pathValue === "goals" &&
        (p === "about.communicationStyle" || p.startsWith("about.communicationStyle."))
      if (ownPath || linkedSrcChanged || householdChanged || commStyleChanged) {
        this.plan = e.detail.plan || this._load()
        this._ensureArray()
        if (this.pathValue === "about.circleOfSupport" && this.ownsHouseholdSyncValue) this._syncFromHousehold()
        this.render()
      }
    }
    document.addEventListener("plan:change", this._onPlanChange)
    // Reordering the section tree changes the Goals section's number, so
    // the goal cards' "12.1"-style badge needs to repaint. Only the goals
    // list depends on section order — other lists can ignore this.
    this._onSidebarOrder = () => { if (this.pathValue === "goals") this.render() }
    document.addEventListener("pcpm:sidebar-order-changed", this._onSidebarOrder)
    // [FirstName] token display in editable content fields (e.g. AI-drafted
    // goal descriptions): show the resolved participant name while viewing,
    // reveal the raw token on focus so edits preserve it, re-resolve on blur.
    // Delegated on the controller root — focusin/focusout bubble (focus/blur
    // don't). Both are no-ops for fields without a data-token-raw shadow, so
    // only tokened content is touched. See _resolveNameTokens.
    this._onFieldFocusIn = (e) => {
      const el = e.target
      if (el && el.dataset && el.dataset.tokenRaw != null) {
        el.value = el.dataset.tokenRaw
        if (el.tagName === "TEXTAREA") this._autoSize(el)
      }
    }
    this._onFieldFocusOut = (e) => {
      const el = e.target
      if (el && el.dataset && el.dataset.tokenRaw != null) {
        el.dataset.tokenRaw = el.value
        el.value = this._resolveNameTokens(el.value)
        if (el.tagName === "TEXTAREA") this._autoSize(el)
      }
    }
    this.element.addEventListener("focusin", this._onFieldFocusIn)
    this.element.addEventListener("focusout", this._onFieldFocusOut)
    // Last-chance flush of a pending list save when the tab hides/closes.
    this._unregFlush = registerFlush(() => this._debSaveDispatch?.flush())
  }

  // Issue #2: keep Circle of Support's people list in sync with
  // About.whoLivesWithMe. Each household entry becomes a Circle row
  // tagged with linkedFromWhoLives = wlw.id; removing the household
  // entry removes the linked row; renaming flows through.
  _syncFromHousehold() {
    if (this.pathValue !== "about.circleOfSupport") return
    const about = (this.plan && this.plan.about) || {}
    const wlw   = Array.isArray(about.whoLivesWithMe) ? about.whoLivesWithMe : []
    const cos   = this._getArr()
    if (!Array.isArray(cos)) return
    // Drop linked rows whose household source no longer exists.
    const liveIds = new Set(wlw.map(w => w?.id).filter(v => v != null))
    let changed = false
    for (let i = cos.length - 1; i >= 0; i--) {
      const r = cos[i]
      if (r && r.linkedFromWhoLives != null && !liveIds.has(r.linkedFromWhoLives)) {
        cos.splice(i, 1)
        changed = true
      }
    }
    // Upsert one Circle row per household member. We surface the
    // age-prefixed name verbatim from the household row (e.g.
    // "[8] Ava Hernandez") so the two stay visually identical.
    wlw.forEach(w => {
      if (!w || w.id == null) return
      const displayName = this._cosNameFromHouseholdRow(w)
      const rel = this._cosRelationshipFromChip(w.relationship || this._roleFromText(w.text))
      const existing = cos.find(r => r && r.linkedFromWhoLives === w.id)
      if (existing) {
        if (existing.name !== displayName) { existing.name = displayName; changed = true }
        // Relationship stays editable — don't clobber a user choice.
        if (!existing.relationship) { existing.relationship = rel; changed = true }
        return
      }
      // Adopt an existing UNLINKED row that already names this person (a
      // manual Circle entry, or a legacy .sdp row saved without links)
      // instead of pushing a second row for them — this is what stops the
      // "Circle of Support repeats Who-Lives names" duplication on open.
      const wantKey = normCosName(displayName)
      const adopt = wantKey
        ? cos.find(r => r && r.linkedFromWhoLives == null && normCosName(r.name) === wantKey)
        : null
      if (adopt) {
        adopt.linkedFromWhoLives = w.id
        adopt.name = displayName
        if (!adopt.relationship) adopt.relationship = rel
        changed = true
        return
      }
      cos.unshift({
        id: Date.now() + Math.random() + cos.length,
        name: displayName,
        relationship: rel,
        linkedFromWhoLives: w.id
      })
      changed = true
    })
    // Fold any residual duplicates (same-link rows, or an unlinked row
    // matching a linked household member) so an already-duplicated plan
    // heals on the next seed/open.
    const beforeLen = cos.length
    dedupeCircle(cos)
    if (cos.length !== beforeLen) changed = true
    if (changed) {
      // Persist quietly — we're seeding, not user-driven; suppress
      // the next plan:change reaction in our own listener so we
      // don't loop.
      this._suppressNextChange = true
      savePlan(this.plan)
      document.dispatchEvent(new CustomEvent("plan:change", {
        bubbles: true,
        detail: { path: this.pathValue, plan: this.plan, source: "cos:sync-household" }
      }))
      this._suppressNextChange = false
    }
  }

  // Pull the display name out of a household row. Prefers
  // firstName + lastName (set by the wizard's family micro-flow);
  // falls back to parsing "[Age] Name (Role)" or "Name (Role)".
  _cosNameFromHouseholdRow(w) {
    const fn = (w.firstName || "").trim()
    const ln = (w.lastName  || "").trim()
    if (fn || ln) {
      // Issue #7: "Name [Age]" in the COS row so it matches the new
      // household format. Role/relationship is captured separately
      // by _cosRelationshipFromChip.
      const ageTag = (w.ageGroup || w.ageYears) ? ` [${w.ageYears != null ? w.ageYears : w.ageGroup}]` : ""
      return `${fn}${fn && ln ? " " : ""}${ln}${ageTag}`.trim()
    }
    // Legacy text-only rows — strip any of the three historical formats
    // ("Name (Role) [Age]", "[Age] Name (Role)", "Name [Age] (Role)")
    // so the COS row shows just the name.
    let t = (w.text || "").trim()
    // remove trailing role
    t = t.replace(/\s*\([^)]+\)\s*$/, "")
    // remove leading or trailing "[Age]"
    t = t.replace(/^\s*\[[^\]]+\]\s*/, "")
    t = t.replace(/\s*\[[^\]]+\]\s*$/, "")
    return t || "(unnamed household member)"
  }

  // Detect a parenthesised role inside a legacy text-only row
  // ("[8] Ava Hernandez (Sister)" → "Sister").
  _roleFromText(s) {
    if (!s) return ""
    const m = String(s).match(/\(([^)]+)\)\s*(\[[^\]]+\])?\s*$/)
    return m ? m[1].trim() : ""
  }

  // Issue #8: deterministic display order for Circle of Support rows.
  // Returns a sorted shallow copy — the underlying plan array is left
  // alone so manual drag-reorder + save semantics still work, and the
  // sort is re-applied on every render.
  _cosOrderForDisplay(items) {
    const rank = (rel) => {
      switch ((rel || "").toLowerCase()) {
        case "family":       return 0
        case "friend":       return 1
        case "professional": return 2
        case "community":    return 3
        case "service dog":  return 4
        case "tbd":          return 5
        default:             return 6  // unset / unknown — bottom
      }
    }
    // Stable sort via index tiebreaker so within-group order is
    // preserved (household-linked rows still lead the Family bucket).
    return items
      .map((it, idx) => ({ it, idx, r: rank(it && it.relationship) }))
      .sort((a, b) => a.r - b.r || a.idx - b.idx)
      .map(x => x.it)
  }

  // Items in the order the user currently SEES them. Circle of Support is
  // grouped by category until the user first drags a row (then the stored
  // order is authoritative); every other list is just its stored order.
  // render() and drop() both use this so drops map to the visible rows.
  _displayItems() {
    const items = this._getArr()
    if (this.pathValue === "about.circleOfSupport" && !this._cosManualOrder()) {
      return this._cosOrderForDisplay(items)
    }
    return items
  }

  _cosManualOrder() {
    return !!(this.plan && this.plan.about && this.plan.about.cosManualOrder)
  }

  // A Circle-of-Support row belongs to THIS instance's filtered group view?
  // Display-only — the stored array is never filtered.
  _cosMatchesFilter(item) {
    const f = this.relationshipFilterValue
    if (!f) return true
    if (this.excludeLinkedValue && item && item.linkedFromWhoLives != null) return false
    const rel = (item && item.relationship) || ""
    // "__rest__" is the never-orphaned bucket: anything not in a named group
    // (Service Dog / TBD / empty / unknown).
    if (f === "__rest__") return !["Family", "Friend", "Professional", "Community"].includes(rel)
    return rel === f
  }

  // Rows the user SEES in this list: the display order, filtered to this group
  // (no filter → the full display order). render() uses this; add/remove/
  // update/drop all still operate on the FULL array via _getArr/_setArr.
  _visibleItems() {
    const items = this._displayItems()
    if (!this.hasRelationshipFilterValue || !this.relationshipFilterValue) return items
    return items.filter(it => this._cosMatchesFilter(it))
  }

  _markCosManualOrder() {
    if (!this.plan.about || typeof this.plan.about !== "object") this.plan.about = {}
    this.plan.about.cosManualOrder = true
  }

  // The household chips use specific relationship labels ("Brother",
  // "Mom", "Pet dog"); map them to the four-category COS taxonomy.
  _cosRelationshipFromChip(chip) {
    const c = (chip || "").toLowerCase()
    if (!c) return "Family"
    if (/(pet|dog|cat)/i.test(c))                                          return "Service Dog"
    if (/(mom|dad|step|brother|sister|grand|aunt|uncle|cousin|spouse|child|son|daughter)/.test(c)) return "Family"
    if (/caregiver|nurse|aide|therapist|teacher|doctor|coach|bcba|slp/.test(c)) return "Professional"
    if (/friend|roommate/.test(c)) return "Friend"
    return "Family"
  }

  disconnect() {
    this._debSaveDispatch?.flush()
    this._unregFlush?.()
    document.removeEventListener("plan:change", this._onPlanChange)
    document.removeEventListener("pcpm:sidebar-order-changed", this._onSidebarOrder)
    this.element.removeEventListener("focusin", this._onFieldFocusIn)
    this.element.removeEventListener("focusout", this._onFieldFocusOut)
  }

  // Bulk Entry — open a paste-box modal where the user can drop a list
  // (one per line) and add them all at once. Two modes:
  //
  // - Single-field (default): one entry per line lands in `text`.
  //   Matches pcp_builder.html#5350 toggleBulkList.
  //
  // - Multi-field (bulkFieldsValue set, "|"-separated field names):
  //   each line is split by `|` and the segments map to the fields
  //   in order. Matches the bulk-add modal at
  //   pcp_builder.html#11256 bulkAddCommRows. Empty segments stay
  //   empty so the user can paste partial rows.
  async openBulk() {
    const items = this._getArr()
    const label = this.element.querySelector("label")?.textContent?.trim().replace(/\s+·.*$/, "") || "Items"
    const multi = this.hasBulkFieldsValue && this.bulkFieldsValue
    const fields = multi ? this.bulkFieldsValue.split("|").map(s => s.trim()).filter(Boolean) : []
    const placeholder = multi
      ? (this.bulkPlaceholderValue || fields.map((f, i) => `${f}${i < fields.length - 1 ? " | " : ""}`).join(""))
      : "Item 1\nItem 2\nItem 3"
    const text = await showBulkModal({
      title:    `Bulk add to "${label}"`,
      subtitle: multi ? `One row per line. Separate columns with "|".` : "One item per line.",
      placeholder,
      multi,
      fields
    })
    if (text == null) return
    const lines = text.split("\n").map(s => s.trim()).filter(Boolean)
    if (!lines.length) return
    lines.forEach(line => {
      const id = Date.now() + Math.random()
      this._newIds.add(id)
      if (multi) {
        const parts = line.split("|").map(s => s.trim())
        const seed = { id }
        fields.forEach((f, i) => {
          let v = parts[i] || ""
          // Snap a pasted CoS relationship to the exact <select> option so
          // the dropdown, stored value, and category icon agree; unknown
          // values fall back to TBD (surfaces the amber review flag).
          if (this.pathValue === "about.circleOfSupport" && f === "relationship" && v) {
            v = COS_RELATIONSHIPS.find(o => o.toLowerCase() === v.toLowerCase()) || "TBD"
          }
          seed[f] = v
        })
        // Grouped Circle lists paste names only — stamp the group's relationship.
        if (this.hasDefaultRelationshipValue && this.defaultRelationshipValue && !seed.relationship) {
          seed.relationship = this.defaultRelationshipValue
        }
        items.push(seed)
      } else {
        items.push({ id, text: line })
      }
    })
    this._setArr(items)
    this._save()
    this.render()
    this._dispatchChange()
  }

  add() {
    // DATA-LOSS FIX: a nested controller (goal resources / references, current
    // services, unmet needs) may hold an edit in its 200 ms debounce. Adding a
    // goal rewrites the whole goals array wholesale, so flush those pending
    // saves and reload FIRST — otherwise the just-typed resource edit, which is
    // only in another controller's memory, is clobbered by our stale array.
    flushAll()
    this.plan = this._load()
    this._ensureArray()
    const items = this._getArr()
    // Seed every field declared in the row template with an empty value,
    // so multi-field rows (e.g. circleOfSupport's name+relationship,
    // communication's 4 textareas) have all their keys present from
    // the start. Single-field rows reduce to { id, text: "" }.
    const seed = { id: Date.now() + Math.random() }
    const tpl = this.rowTemplateTarget.content
    tpl.querySelectorAll("[data-field]").forEach(el => {
      const field = el.dataset.field
      // Use the element's defaultValue or first <option> for selects so
      // unwritten fields don't surprise readers with `undefined`.
      let v = ""
      if (el.tagName === "SELECT") v = el.value || (el.options[0] && el.options[0].value) || ""
      seed[field] = v
    })
    // Grouped Circle lists stamp their own relationship so a new row lands in
    // the right group immediately.
    if (this.hasDefaultRelationshipValue && this.defaultRelationshipValue) {
      seed.relationship = this.defaultRelationshipValue
    }
    items.push(seed)
    this._newIds.add(seed.id)
    this._setArr(items)
    this._save()
    this.render({ focusLast: true })
    this._dispatchChange()
  }

  // Open the Review / Print Preview in a new tab, scrolled to THIS goal's
  // card. Wired only on the Goals list (its card template renders the icon).
  // The goal's index among the rows == its index in plan.goals == the
  // preview's "ref-goal-N" anchor.
  previewGoal(event) {
    event.preventDefault()
    const row = event.currentTarget.closest("[data-row]")
    if (!row || !this.hasListTarget) return
    const idx = Array.from(this.listTarget.querySelectorAll("[data-row]")).indexOf(row)
    if (idx < 0) return
    window.open(`/editor/native/preview#ref-goal-${idx}`, "_blank", "noopener")
  }

  // Preview → editor jump: the goal edit button links to
  // /editor/native/goals#ref-goal-N. Scroll the Nth goal card into view
  // (the editor's cards don't carry the preview's ref-goal ids).
  _scrollToHashGoal() {
    const m = /^#ref-goal-(\d+)$/.exec(window.location.hash || "")
    if (!m) return
    const idx = parseInt(m[1], 10)
    const start = Date.now()
    // The row exists only after render, AND only becomes scrollable once
    // the Goals section is un-hidden (offsetParent is null while hidden).
    const rowAt = () => {
      const rows = this.hasListTarget ? this.listTarget.querySelectorAll("[data-row]") : []
      const row = rows[idx]
      return (row && row.offsetParent !== null) ? row : null
    }
    const land = (row) => {
      row.scrollIntoView({ behavior: "smooth", block: "start" })
      row.classList.add("dl-jump-flash")
      setTimeout(() => row.classList.remove("dl-jump-flash"), 1700)
      // Re-assert twice to defeat Turbo's scroll-restoration on the fresh
      // page visit (which can yank us back to the top after we scroll).
      setTimeout(() => { const r = rowAt(); if (r) r.scrollIntoView({ block: "start" }) }, 300)
      setTimeout(() => { const r = rowAt(); if (r) r.scrollIntoView({ block: "start" }) }, 650)
    }
    const wait = () => {
      const row = rowAt()
      if (row) { land(row); return }
      if (Date.now() - start > 3000) return
      setTimeout(wait, 90)
    }
    setTimeout(wait, 50)
  }

  // ★ Add this goal to the user's library (serial PCP XXX.YY). Captures the
  // reusable fields (heading / description / timeline / domain) — never vendor or
  // frequency. A goal that already came from a library family (goal.libXxx) asks
  // whether to save a new version or a new goal; the chosen serial is remembered
  // back on the goal so a later re-save offers versioning again.
  addGoalToLibrary(event) {
    event.preventDefault()
    const row = event.currentTarget.closest("[data-row]")
    if (!row) return
    const rid = row.dataset.id
    const plan = this._load()
    const goal = (Array.isArray(plan.goals) ? plan.goals : []).find(g => String(g.id) === String(rid))
    if (!goal) return
    // References + candidate vendors ride along into the goal FAMILY (shared
    // across variations). Vendors are flattened from every resource on the goal.
    const references = (Array.isArray(goal.references) ? goal.references : [])
      .map(r => ({ id: r.id, title: (r.title || "").trim(), url: (r.url || "").trim(), chip: (r.chip || "").trim() }))
      .filter(r => r.title || r.url)
    const vendors = (Array.isArray(goal.resources) ? goal.resources : [])
      .flatMap(res => (res && Array.isArray(res.vendors)) ? res.vendors : [])
      .map(v => ({ id: v.id, name: (v.name || "").trim(), phone: (v.phone || "").trim(), email: (v.email || "").trim(), website: (v.website || "").trim() }))
      .filter(v => v.name)
    const fields = {
      heading:     (goal.heading || "").trim(),
      description: goal.description || "",
      timeline:    (goal.timeline || "").trim(),
      domain:      (goal.domain || "").trim(),
      group:       goal.category || "other",
      source:      goal.source || "Custom",
      references,
      vendors,
    }
    const fromXxx = (typeof goal.libXxx === "number") ? goal.libXxx : null
    saveGoalToLibrary(fields, fromXxx, (serial) => {
      const p = this._load()
      const g = (Array.isArray(p.goals) ? p.goals : []).find(x => String(x.id) === String(rid))
      if (!g) return
      g.libSerial = serial
      const m = String(serial).match(/PCP\s+(\d+)/)
      if (m) g.libXxx = parseInt(m[1], 10)
      // Minimal write: only the (freshly-loaded) goals array — arrays merge
      // wholesale, so this touches nothing outside plan.goals.
      savePlan({ goals: p.goals })
    })
  }

  // Move a goal into the Barriers section — its description + resources
  // carry over. Reloads the freshest plan first so recent barrier edits
  // aren't clobbered, then broadcasts so both lists + the preview refresh.
  moveGoalToBarrier(event) {
    event.preventDefault()
    const row = event.currentTarget.closest("[data-row]")
    if (!row) return
    const rid = row.dataset.id
    flushAll()                    // persist pending resource/reference edits before the reload
    this.plan = this._load()
    const goals = Array.isArray(this.plan.goals) ? this.plan.goals : []
    const goal = goals.find(g => String(g.id) === rid)
    if (!goal) return
    const label = (goal.heading || goal.description || "this goal").trim().slice(0, 60)
    if (!window.confirm(`Move "${label}" to the Barriers section?`)) return
    if (!Array.isArray(this.plan.barriers)) this.plan.barriers = []
    this.plan.barriers.push({
      id: goal.id,
      description: goal.description || goal.heading || "",
      strategy: "",
      resources: Array.isArray(goal.resources) ? goal.resources : [],
      references: Array.isArray(goal.references) ? goal.references : []
    })
    this.plan.goals = goals.filter(g => String(g.id) !== rid)
    // this.plan was just reloaded fresh (above) and this touches TWO arrays
    // (goals + barriers), so a full write is correct and safe here; the "*"
    // broadcast refreshes every other list's in-memory copy.
    savePlan(this.plan)
    document.dispatchEvent(new CustomEvent("plan:change", {
      bubbles: true, detail: { path: "*", plan: this.plan, source: "dynamic-list" }
    }))
    this.render()
  }

  // Reverse of moveGoalToBarrier — move a barrier back into the Goals
  // section (its description + resources carry over).
  moveBarrierToGoal(event) {
    event.preventDefault()
    const row = event.currentTarget.closest("[data-row]")
    if (!row) return
    const rid = row.dataset.id
    flushAll()                    // persist pending resource/reference edits before the reload
    this.plan = this._load()
    const barriers = Array.isArray(this.plan.barriers) ? this.plan.barriers : []
    const barrier = barriers.find(b => String(b.id) === rid)
    if (!barrier) return
    const label = (barrier.description || barrier.strategy || "this barrier").trim().slice(0, 60)
    if (!window.confirm(`Move "${label}" to the Goals section?`)) return
    if (!Array.isArray(this.plan.goals)) this.plan.goals = []
    this.plan.goals.push({
      id: barrier.id,
      heading: "",
      description: barrier.description || barrier.strategy || "",
      timeline: "",
      resources: Array.isArray(barrier.resources) ? barrier.resources : [],
      references: Array.isArray(barrier.references) ? barrier.references : []
    })
    this.plan.barriers = barriers.filter(b => String(b.id) !== rid)
    // Fresh reload + two-array change → full write is correct; "*" refreshes
    // the other lists.
    savePlan(this.plan)
    document.dispatchEvent(new CustomEvent("plan:change", {
      bubbles: true, detail: { path: "*", plan: this.plan, source: "dynamic-list" }
    }))
    this.render()
  }

  remove(event) {
    const row = event.currentTarget.closest("[data-row]")
    if (!row) return
    const rid = row.dataset.id
    // Same clobber guard as add(): flush pending nested edits + reload so the
    // wholesale array write below keeps every OTHER row's in-flight edits.
    flushAll()
    this.plan = this._load()
    this._ensureArray()
    const items = this._getArr()
    const removed = items.find(it => String(it.id) === rid)
    if (removed) this._newIds.delete(removed.id)
    this._setArr(items.filter(it => String(it.id) !== rid))
    // Cross-list link: if this row is a Circle-of-Support entry that
    // mirrors a "Who Lives With Me?" item, also drop the source so the
    // next sync doesn't immediately re-create the row we just removed.
    if (removed && removed.linkedFromWhoLives != null &&
        this.plan?.about && Array.isArray(this.plan.about.whoLivesWithMe)) {
      this.plan.about.whoLivesWithMe = this.plan.about.whoLivesWithMe.filter(
        w => w && w.id !== removed.linkedFromWhoLives
      )
    }
    this._save()
    this.render()
    this._dispatchChange()
  }

  update(event) {
    const input = event.currentTarget
    const row = input.closest("[data-row]")
    if (!row) return
    const rid = row.dataset.id
    const items = this._getArr()
    const it = items.find(x => String(x.id) === rid)
    if (!it) return
    // Multi-field rows declare data-field on each input/select/textarea;
    // single-field rows default to `text` for backwards compat.
    const field = input.dataset.field || "text"
    it[field] = input.type === "checkbox" ? !!input.checked : input.value
    // The field shows the RAW [FirstName] token while focused, so input.value
    // is the raw text — keep the shadow in sync so blur re-resolves correctly
    // and we never persist the display-resolved name over the token.
    if (input.dataset && input.dataset.tokenRaw != null) input.dataset.tokenRaw = input.value
    this._setArr(items)
    // Editing the row removes its NEW pill (matches the legacy where
    // appState._newIds entries clear once the user touches the row).
    if (this._newIds.has(it.id)) {
      this._newIds.delete(it.id)
      const newBadge = row.querySelector("[data-new-badge]")
      if (newBadge) newBadge.hidden = true
    }
    // Auto-grow textareas as the user types so long entries stay
    // visible — matches pcp_builder.html#5593 oninput resize.
    if (input.tagName === "TEXTAREA") this._autoSize(input)
    // Repaint TBD highlighting now that the field value changed.
    this._refreshRowTbd(row)
    // Re-paint parse-mode chips (relationship, age, school type, grade)
    // so they follow the user's edits live.
    if (this.parseModeValue) this._paintRowParseChips(row)
    this._saveDispatchDebounced()
    // In a grouped Circle list, changing a row's relationship re-homes it to
    // another group — re-render so it leaves this list. (A <select> change
    // doesn't hold text focus, so re-rendering is safe.)
    if (input.dataset.field === "relationship" && this.hasRelationshipFilterValue) {
      this.render()
    }
  }

  moveUp(event)   { this._move(event, -1) }
  moveDown(event) { this._move(event,  1) }

  // ---- Drag-reorder ----
  // Rows opt in by setting `draggable="true"` + wiring
  // `dragstart/dragover/drop/dragend/dragleave` to these actions in
  // their row template (see _circle_of_support.html.erb). Other lists
  // that don't add those attributes are unaffected.
  dragStart(event) {
    // The whole card is draggable for reordering, but a drag that STARTS
    // inside a form field should select text / interact — not drag the card.
    // Cancel the drag in that case so highlighting text works normally.
    if (event.target.closest("input, textarea, select, button, a, [contenteditable]")) {
      event.preventDefault()
      return
    }
    const row = event.currentTarget
    this._dragFromId = row.dataset.id
    row.classList.add("cos-row-dragging")
    event.dataTransfer.effectAllowed = "move"
    // Some browsers need a non-empty payload to start the drag.
    try { event.dataTransfer.setData("text/plain", String(this._dragFromId)) } catch (_) {}
  }

  dragOver(event) {
    if (this._dragFromId == null) return
    event.preventDefault()                    // permit drop
    event.dataTransfer.dropEffect = "move"
    const row = event.currentTarget
    if (row.dataset.id === this._dragFromId) return
    // Visual cue: highlight whichever edge of the row the cursor
    // is closer to so the user knows where the drop will land.
    const rect = row.getBoundingClientRect()
    const before = (event.clientY - rect.top) < rect.height / 2
    this.listTarget.querySelectorAll(".cos-row-drop-before, .cos-row-drop-after")
      .forEach(r => r.classList.remove("cos-row-drop-before", "cos-row-drop-after"))
    row.classList.add(before ? "cos-row-drop-before" : "cos-row-drop-after")
  }

  dragLeave(event) {
    event.currentTarget.classList.remove("cos-row-drop-before", "cos-row-drop-after")
  }

  drop(event) {
    if (this._dragFromId == null) return
    event.preventDefault()
    const row = event.currentTarget
    const fromId = this._dragFromId
    const toId   = row.dataset.id
    this._clearDragMarkers()
    if (fromId === toId) return

    // Reorder against the VISIBLE order so the drop lands where the user
    // sees it (CoS is category-grouped until the first manual drag).
    const items = this._displayItems()
    const fromIdx = items.findIndex(x => String(x.id) === fromId)
    let   toIdx   = items.findIndex(x => String(x.id) === toId)
    if (fromIdx < 0 || toIdx < 0) return

    const rect = row.getBoundingClientRect()
    const before = (event.clientY - rect.top) < rect.height / 2
    // Drop ABOVE the target inserts at its index; BELOW inserts at
    // index + 1 — both adjusted when dragging downward to account
    // for the spliced-out source slot.
    let insertAt = before ? toIdx : toIdx + 1
    if (fromIdx < insertAt) insertAt -= 1

    const [moved] = items.splice(fromIdx, 1)
    items.splice(insertAt, 0, moved)
    this._setArr(items)
    // First manual drag bakes in the current (grouped) order + this move
    // and stops the auto-regrouping from then on ("group once, then free").
    if (this.pathValue === "about.circleOfSupport") this._markCosManualOrder()
    this._save()
    this.render()
    this._dispatchChange()
  }

  dragEnd(event) {
    event.currentTarget.classList.remove("cos-row-dragging")
    this._clearDragMarkers()
    this._dragFromId = null
  }

  _clearDragMarkers() {
    this.listTarget.querySelectorAll(".cos-row-drop-before, .cos-row-drop-after, .cos-row-dragging")
      .forEach(r => r.classList.remove("cos-row-drop-before", "cos-row-drop-after", "cos-row-dragging"))
  }

  // Wipe every row in this list after a confirm prompt. Matches
  // the legacy "Clear All" button at pcp_builder.html#5344 — same
  // confirm copy ("Clear all <label>? This cannot be undone.").
  clearAll(event) {
    event?.preventDefault?.()
    const label = this.element.querySelector("label")?.textContent?.trim() || "items"
    if (!confirm(`Clear all ${label}? This cannot be undone.`)) return
    this._newIds.clear()
    this._setArr([])
    this._save()
    this.render()
    this._dispatchChange()
  }

  _move(event, delta) {
    const row = event.currentTarget.closest("[data-row]")
    if (!row) return
    const rid = row.dataset.id
    const items = this._getArr()
    const idx = items.findIndex(x => String(x.id) === rid)
    const next = idx + delta
    if (idx < 0 || next < 0 || next >= items.length) return
    const tmp = items[idx]; items[idx] = items[next]; items[next] = tmp
    this._setArr(items)
    this._save()
    this.render()
    this._dispatchChange()
  }

  // The Goals section's number, counted EXACTLY as the preview/PDF numbers it
  // so the editor "N.1" goal badge matches the PDF's "Goal N.1" H2 and ref
  // chips (there's a "Preview this goal in the PDF" link tying the two). We
  // read the sidebar DOM (the <li data-section-key> rows, whose order is the
  // server's SECTION_KEYS, mutated live by drag-reorder) rather than
  // plan.meta.sectionOrder, which can drift out of sync and mis-number.
  //
  // The preview numbers only CONTENT sections: pcpSectionOrder() drops keys
  // absent from its SECTIONS map (openQuestions, facilitator) and render()
  // additionally skips client/review/brochures — so those must not be counted
  // here either (the sidebar renders all 19 SECTION_KEYS, incl. openQuestions
  // and client). Sections excluded from the output (e.g. the Behavior
  // Communication Chart when "Behavioral Communication" isn't selected) are
  // dropped via isSectionIncluded. Falls back to the canonical order when the
  // sidebar isn't in the DOM.
  _goalsSectionNum() {
    const plan = this.plan || {}
    const SKIP = new Set(["openQuestions", "client", "review", "facilitator", "brochures"])
    const countable = (k) => !SKIP.has(k) && isSectionIncluded(plan, k)
    const rows = document.querySelectorAll('[data-sidebar-reorder-target="row"]')
    if (rows.length) {
      const keys = Array.from(rows).map(r => r.dataset.sectionKey).filter(Boolean).filter(countable)
      const idx = keys.indexOf("goals")
      if (idx >= 0) return idx + 1
    }
    const idx = DEFAULT_SECTION_ORDER.filter(countable).indexOf("goals")
    return idx >= 0 ? idx + 1 : 11
  }

  render({ focusLast = false } = {}) {
    // Preserve the surrounding scroll position across the full list
    // rebuild — otherwise removing a row (issue #38) momentarily collapses
    // the list and the browser snaps the editor pane back to the top.
    // Skipped when focusLast (add), where we intentionally focus/scroll to
    // the new row.
    const scroller = this._scrollContainer()
    const savedScroll = scroller ? scroller.scrollTop : null
    // Circle of Support is auto-grouped by category (Family > Friend >
    // Professional > …) ONLY until the user first drags a row; after that
    // plan.about.cosManualOrder locks in their manual order. See
    // _displayItems / drop. Other lists use their stored order as-is.
    let items = this._visibleItems()
    // Folded "Other / To be determined" catch-all: hide the whole card unless
    // it actually holds a stray (Service Dog / TBD / unknown) row.
    if (this.relationshipFilterValue === "__rest__") {
      this.element.style.display = items.length ? "" : "none"
    }
    if (this.hasEmptyTarget) this.emptyTarget.style.display = items.length === 0 ? "" : "none"
    if (this.hasClearAllTarget) this.clearAllTarget.hidden = items.length === 0
    if (this.hasCountTarget) {
      // "· 3 members" — pluralize naively: 1 → singular (strip trailing "s"
      // if present), N → plural as-given. Hide entirely on 0 so the label
      // doesn't read "· 0 members" right after the user clears the list.
      if (items.length === 0) {
        this.countTarget.textContent = ""
      } else {
        const label = items.length === 1
          ? this.itemsLabelValue.replace(/s$/i, "")
          : this.itemsLabelValue
        this.countTarget.textContent = `· ${items.length} ${label}`
      }
    }

    // Remove existing rows (everything except the template).
    this.listTarget.querySelectorAll("[data-row]").forEach(r => r.remove())

    const tpl = this.rowTemplateTarget.content
    items.forEach((it, idx) => {
      const node = tpl.cloneNode(true)
      const row = node.querySelector("[data-row]")
      row.dataset.id = it.id
      // Sub-section number badge. Goals show a SECTION-relative number
      // ("12.1"): the Goals section's position among the included sections,
      // then the goal's 1-based position — matching the PDF's sub-numbering.
      // Other lists (or templates without [data-row-number]) just show the
      // plain index / no-op. Re-runs on every render, so add / remove /
      // reorder / enable-disable renumbers automatically.
      const numEl = row.querySelector("[data-row-number]")
      if (numEl) {
        const prefix = this.pathValue === "goals" ? `${this._goalsSectionNum()}.` : ""
        numEl.textContent = prefix + String(idx + 1)
      }
      // Hydrate every [data-field] element in the row from the item.
      // Single-field rows have just data-field="text"; multi-field rows
      // (circleOfSupport, communication) iterate every field.
      row.querySelectorAll("[data-field]").forEach(el => {
        const field = el.dataset.field
        const val   = it[field]
        if (el.type === "checkbox") {
          el.checked = !!val
        } else {
          const raw = val == null ? "" : String(val)
          if (this._hasNameToken(raw)) {
            // Content field carrying a [FirstName] token (e.g. an AI-drafted
            // goal description). Keep the raw token in data-token-raw + the
            // stored array; DISPLAY the resolved name when Redact is off and
            // the field isn't being edited (focus reveals the raw token).
            el.dataset.tokenRaw = raw
            el.value = (document.activeElement === el) ? raw : this._resolveNameTokens(raw)
          } else {
            if (el.dataset.tokenRaw != null) delete el.dataset.tokenRaw
            el.value = raw
          }
        }
        if (field === "text" && this.hasPlaceholderValue && this.placeholderValue) {
          el.placeholder = this.placeholderValue
        }
      })
      // Disable up on first row, down on last row.
      const up   = row.querySelector("[data-row-up]")
      const down = row.querySelector("[data-row-down]")
      if (up)   up.disabled   = idx === 0
      if (down) down.disabled = idx === items.length - 1
      // Show NEW pill on rows added this session that the user
      // hasn't edited yet; show TBD pill + amber styling when any
      // field still contains "TBD". Both badges live on the row
      // template behind data-new-badge / data-tbd-badge.
      const newBadge = row.querySelector("[data-new-badge]")
      if (newBadge) newBadge.hidden = !this._newIds.has(it.id)
      // Two flavours of "linked" rows:
      //   - CoS rows that mirror a Who Lives With Me entry
      //     (linkedFromWhoLives) — name flows from About
      //   - Current Services rows added via the Recommended picker
      //     (linkedType) — text is the service type, schedule /
      //     ratio / comment are resolved live from goals / barriers
      // Both surface a small link badge + gray styling so the user
      // can tell them apart from the manually-entered rows, and the
      // bound text input is pinned readonly because edits would
      // break the link.
      const linkBadge = row.querySelector("[data-link-badge]")
      const isLinked  = !!(it.linkedFromWhoLives || it.linkedType)
      row.classList.toggle("cos-linked", isLinked)
      if (linkBadge) linkBadge.hidden = !isLinked
      const lockField = row.querySelector('[data-field="name"]')
                      || row.querySelector('[data-field="text"]')
      if (lockField) {
        if (isLinked) lockField.setAttribute("readonly", "readonly")
        else          lockField.removeAttribute("readonly")
      }
      // For Current Services rows linked via linkedType, the RC /
      // Generic funding tag is OWNED by the goal-or-barrier resource
      // we link to — auto-select the matching option in the tag
      // dropdown and lock it so the user can't drift the two apart.
      // Mirrors the legacy resolveLinkedService at pcp_builder.html#4003.
      const tagSelect = row.querySelector('[data-field="tag"]')
      if (tagSelect) {
        if (it.linkedType) {
          const src = this._resolveLinkedSource(it.linkedType)
          if (src) {
            tagSelect.value = src.tag === "rc" ? "rc" : "general"
            tagSelect.disabled = true
            tagSelect.title = `Linked to ${src.sourceRef || "Goal / Barrier"} — funding tag follows the source`
          }
        } else {
          tagSelect.disabled = false
          tagSelect.removeAttribute("title")
        }
      }
      this.listTarget.appendChild(node)
    })
    // Apply TBD + auto-resize after the rows are in the DOM so
    // scrollHeight measurements are real (they're 0 inside a
    // DocumentFragment).
    this.listTarget.querySelectorAll("[data-row]").forEach(r => {
      r.querySelectorAll("textarea").forEach(t => this._autoSize(t))
      this._refreshRowTbd(r)
      if (this.parseModeValue) this._paintRowParseChips(r)
    })
    renderIcons(this.element)
    // Restore the pre-rebuild scroll position for EVERY rebuild — add,
    // remove, reorder (issue #38) — so the editor pane never yanks.
    if (scroller && savedScroll != null) scroller.scrollTop = savedScroll
    if (focusLast) {
      const rows = this.listTarget.querySelectorAll("[data-row]")
      const last = rows[rows.length - 1]
      if (last) {
        const input = last.querySelector("[data-field=text]") || last.querySelector("[data-field]")
        // preventScroll so focusing the new row doesn't scroll it into
        // view and undo the scroll restore above.
        if (input) input.focus({ preventScroll: true })
      }
    }
  }

  // Nearest scrollable ancestor of this list — used to preserve scroll
  // position across a full render() rebuild. Falls back to the document
  // scroller when the list isn't inside an overflow container.
  _scrollContainer() {
    let el = this.element && this.element.parentElement
    while (el && el !== document.body) {
      const oy = getComputedStyle(el).overflowY
      if ((oy === "auto" || oy === "scroll") && el.scrollHeight > el.clientHeight) return el
      el = el.parentElement
    }
    return document.scrollingElement || document.documentElement
  }

  // ---- [FirstName] token display in editable content --------------------
  // Goal descriptions (and any content field) may hold the literal
  // [FirstName] token so the PDF can redact PII. In the editor we DISPLAY
  // the participant's real name when Redact PHI is OFF, but keep the raw
  // token in storage (revealed on focus for editing) so redaction + the PDF
  // stay correct. No-op for any value without the token, so only tokened
  // content is touched. Name resolution mirrors the print preview's
  // _expandPlaceholders (firstName field, then first word of full name).
  _hasNameToken(str) { return typeof str === "string" && /\[First\s*Name\]/i.test(str) }

  _resolveNameTokens(str) {
    if (!this._hasNameToken(str)) return str
    const plan = this.plan || this._load()
    if (plan && plan.meta && plan.meta.redacted) return str
    const c = (plan && plan.client) || {}
    const name = (c.firstName && c.firstName.trim()) ||
                 (c.name && c.name.trim() && c.name.trim().split(/\s+/)[0]) ||
                 ""
    return name ? str.replace(/\[First\s*Name\]/gi, name) : str
  }

  // Re-resolve every token-bearing field's DISPLAY from its raw shadow,
  // in place (no row rebuild). Skips the field being edited so we never
  // yank a token back over the user's cursor mid-keystroke.
  _reresolveNameTokenFields() {
    this.element.querySelectorAll("[data-field]").forEach(el => {
      if (el.dataset && el.dataset.tokenRaw != null && document.activeElement !== el) {
        el.value = this._resolveNameTokens(el.dataset.tokenRaw)
        if (el.tagName === "TEXTAREA") this._autoSize(el)
      }
    })
  }

  // Re-flow a textarea so its rendered height matches its content.
  // Mirrors the inline `this.style.height='auto';
  // this.style.height=this.scrollHeight+'px'` snippet used on every
  // editable textarea in pcp_builder.html, with four tweaks the
  // legacy got away without:
  //
  //   - scrollHeight excludes the textarea's border, but Tailwind's
  //     preflight uses box-sizing: border-box so the height we set
  //     includes border + padding + content. Without the +Δ the
  //     border eats into the content area and the text clips.
  //   - empty / placeholder-only textareas report a tiny scrollHeight
  //     and end up shorter than a single line; we floor at 36px.
  //   - `transition-all` on the textarea animates height changes,
  //     so we'd read an in-between height. Snap transition off
  //     around the measurement.
  //   - scrollHeight is a function of the textarea's CURRENT width,
  //     and flex layout doesn't always settle in one frame (web
  //     fonts can shift wrap a couple frames later). Attach a
  //     ResizeObserver per textarea that re-runs the calculation
  //     whenever the rendered width changes, so the row grows to
  //     fit the wrapped text the moment layout reaches its final
  //     state — no manual refresh.
  _autoSize(el) {
    if (!el || el.tagName !== "TEXTAREA") return
    const prevTransition = el.style.transition
    el.style.transition = "none"
    el.style.height = "auto"
    const borderY = (el.offsetHeight - el.clientHeight) || 2
    el.style.height = `${Math.max(el.scrollHeight + borderY, 36)}px`
    void el.offsetHeight
    el.style.transition = prevTransition
    // First call: hook a ResizeObserver so width changes (initial
    // paint, font load, window resize, sidebar toggle) re-run the
    // resize. Stored on the element so we don't double-observe.
    if (!el._dlSizer && typeof ResizeObserver !== "undefined") {
      let lastWidth = el.offsetWidth
      el._dlSizer = new ResizeObserver(() => {
        const w = el.offsetWidth
        if (w === lastWidth) return
        lastWidth = w
        this._autoSize(el)
      })
      el._dlSizer.observe(el)
    }
  }

  // Toggle the amber TBD styling on the row + its fields when any
  // textarea/input/select still has "TBD" in it. Port of
  // refreshRowTBD at pcp_builder.html#4275. Each TBD field also
  // gets an amber border / bg via .dl-tbd-field; the row gets
  // .dl-tbd-row + the badge shows via data-tbd-badge.
  _refreshRowTbd(row) {
    if (!row) return
    let rowHas = false
    row.querySelectorAll("input, textarea, select").forEach(f => {
      const isTbd = TBD_RX.test(f.value || "")
      f.classList.toggle("dl-tbd-field", isTbd)
      if (isTbd) rowHas = true
    })
    row.classList.toggle("dl-tbd-row", rowHas)
    const badge = row.querySelector("[data-tbd-badge]")
    if (badge) badge.hidden = !rowHas
  }

  // Updates the two chip slots (data-parse-chip-a / -chip-b) inside
  // a row based on the current parseMode. "lwm" pulls the
  // relationship from the trailing "(Mother)" parens; "school"
  // pulls the school type ("Elementary"/"Middle"/...) and grade
  // ("4th", "Kindergarten", "Senior", ...) from anywhere in the
  // text. Both modes leave the textarea content unchanged — the
  // chips are display-only labels driven from the raw text.
  _paintRowParseChips(row) {
    if (!row) return
    const chipA = row.querySelector("[data-parse-chip-a]")
    const chipB = row.querySelector("[data-parse-chip-b]")
    if (!chipA && !chipB) return
    const txtEl = row.querySelector("[data-field=text]")
    const raw   = (txtEl?.value || "").trim()
    let label = ""
    let color = ""
    let secondLabel = ""
    if (this.parseModeValue === "lwm") {
      // Canonical format: "Name [Age][Relationship]". Falls back to
      // the older "(Relationship)[Age]" form so existing data still
      // chips correctly. Heuristic: brackets that look like an age
      // (contain a digit or words like year/month/old) are age;
      // the other bracket is relationship.
      const brackets = [...raw.matchAll(/\[([^\]]+)\]/g)].map(x => x[1].trim())
      const parens   = [...raw.matchAll(/\(([^()]+)\)/g)].map(x => x[1].trim())
      const looksAge = (s) => /\d/.test(s) || /\byear|\bmonth|\bold\b/i.test(s)
      let relRaw = "", ageRaw = ""
      if (brackets.length >= 2) {
        const [a, b] = brackets
        if (looksAge(a) && !looksAge(b))      { ageRaw = a; relRaw = b }
        else if (!looksAge(a) && looksAge(b)) { relRaw = a; ageRaw = b }
        else                                  { ageRaw = a; relRaw = b }
      } else if (brackets.length === 1) {
        if (looksAge(brackets[0])) ageRaw = brackets[0]
        else                       relRaw = brackets[0]
      }
      if (!relRaw && parens.length) relRaw = parens[0]
      const role = relRaw ? DL_LWM_RELATIONSHIPS.find(r => r.match.test(relRaw)) : null
      if (role) { label = role.label; color = role.color }
      else if (relRaw) { label = relRaw; color = "slate" }
      if (ageRaw) secondLabel = ageRaw
    } else if (this.parseModeValue === "school") {
      const type = DL_SCHOOL_TYPES.find(t => t.match.test(raw))
      const gradeM = raw.match(DL_GRADE_RX)
      if (type)   { label = type.label;  color = type.color }
      if (gradeM) { secondLabel = gradeM[0].trim() }
    }
    if (chipA) {
      if (label) {
        chipA.textContent = label
        chipA.dataset.color = color
        chipA.hidden = false
      } else {
        chipA.hidden = true
      }
    }
    if (chipB) {
      if (secondLabel) {
        chipB.textContent = secondLabel
        chipB.hidden = false
      } else {
        chipB.hidden = true
      }
    }
  }

  // Walks plan.goals and plan.barriers resources looking for the
  // RC / Generic resource whose `type` (or `text`) matches the linked
  // type. Returns { tag, sourceRef } or null when nothing matches.
  // Used by render() to auto-select + lock the funding tag on
  // currentServices rows added via the Recommended picker.
  _resolveLinkedSource(linkedType) {
    if (!linkedType) return null
    const matches = (r, sourceRef) => {
      if (!r) return null
      if (r.tag === "unmet") return null
      if (!matchesServiceLink(r, linkedType)) return null
      return { tag: r.tag || "general", sourceRef }
    }
    const goals = this.plan?.goals || []
    for (let gi = 0; gi < goals.length; gi++) {
      const rs = goals[gi].resources || []
      for (const r of rs) {
        const hit = matches(r, `Goal ${gi + 1}`)
        if (hit) return hit
      }
    }
    const bars = this.plan?.barriers || []
    for (let bi = 0; bi < bars.length; bi++) {
      const rs = bars[bi].resources || []
      for (const r of rs) {
        const hit = matches(r, `Barrier ${bi + 1}`)
        if (hit) return hit
      }
    }
    return null
  }

  // ---- helpers ----
  _load() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY)
      return raw ? JSON.parse(raw) : {}
    } catch (_) { return {} }
  }
  _save() {
    syncWhoLivesIntoCircle(this.plan)
    // Persist ONLY this list's own path — NEVER the whole this.plan.
    // this.plan is refreshed on plan:change only for our own path (to keep
    // per-keystroke edits cheap), so it goes STALE for other sections. And
    // savePlan/deepMerge replaces arrays wholesale, so writing the full
    // stale plan would REVERT edits made in every other array section
    // (goals, barriers, other day-in-life slots, …) — a data-loss clobber.
    // Scoping the write to our own array makes a stale this.plan harmless.
    const patch = this._pathPatch(this.pathValue, this._getArr())
    // Circle of Support also owns the household link mirrored onto
    // about.whoLivesWithMe by syncWhoLivesIntoCircle — persist it too.
    if (this.pathValue === "about.circleOfSupport") {
      const wlw = this.plan && this.plan.about && this.plan.about.whoLivesWithMe
      if (Array.isArray(wlw)) (patch.about = patch.about || {}).whoLivesWithMe = wlw
    }
    savePlan(patch)
  }

  // Build a nested object { a: { b: value } } from a dotted path "a.b", so
  // savePlan merges only that leaf and touches nothing else.
  _pathPatch(path, value) {
    const parts = String(path).split(".")
    const root = {}
    let node = root
    for (let i = 0; i < parts.length - 1; i++) node = (node[parts[i]] = {})
    node[parts[parts.length - 1]] = value
    return root
  }
  // Debounced persist + broadcast for per-keystroke row text edits. The row
  // model + local chips are updated synchronously by update(); only the save
  // and the cross-section plan:change are coalesced. Flushed on disconnect.
  _saveDispatchDebounced() {
    ;(this._debSaveDispatch ||= debounce(() => { this._save(); this._dispatchChange() }, 200, { dirty: true }))()
  }
  _ensureArray() {
    // Walk the dotted path, creating missing intermediate objects, and
    // ensure the terminal slot is an array.
    const parts = this.pathValue.split(".")
    const last = parts.pop()
    let node = this.plan
    parts.forEach(k => {
      if (!node[k] || typeof node[k] !== "object") node[k] = {}
      node = node[k]
    })
    if (!Array.isArray(node[last])) node[last] = []
  }
  _getArr() {
    const parts = this.pathValue.split(".")
    return parts.reduce((o, k) => (o && o[k] !== undefined) ? o[k] : undefined, this.plan) || []
  }
  _setArr(arr) {
    const parts = this.pathValue.split(".")
    const last = parts.pop()
    let node = this.plan
    parts.forEach(k => { node = node[k] })
    node[last] = arr
  }
  _dispatchChange() {
    // Mark self-fired so _onPlanChange can ignore the bounce-back.
    // Dispatch is synchronous, so the flag toggles within one tick.
    this._suppressNextChange = true
    document.dispatchEvent(new CustomEvent("plan:change", {
      bubbles: true,
      detail: { path: this.pathValue, plan: this.plan, source: "dynamic-list" }
    }))
    this._suppressNextChange = false
  }
}
